<script setup>
/**
 * DevKitConversationPipeline — widget de oportunidades no painel do contato.
 *
 * Recebe `contactId`/`conversationId`, busca as oportunidades do contato
 * (`GET .../pipelines/opportunities?contact_id=`) via store e as exibe num
 * AccordionItem "Funil / Oportunidades": pipeline · estágio (cor por behavior)
 * · valor, com realce na oportunidade da conversa atual. Clicar numa
 * oportunidade abre o PipelineOpportunityDialog (que tem o "Ver conversa").
 *
 * Se não houver oportunidades, NÃO renderiza nada (não polui o painel). É o
 * único componente novo plugado no core (1 import + 1 tag em ContactPanel.vue).
 */
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import PipelineOpportunityDialog from './PipelineOpportunityDialog.vue';
import { formatMoney } from './utils/currency';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: undefined,
  },
  conversationId: {
    type: [Number, String],
    default: undefined,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();

const accountId = route.params.accountId;

const opportunities = ref([]);

// Behavior -> apresentação do estágio (cor + ícone). Mesma semântica do board.
const BEHAVIOR_ACCENTS = {
  won: {
    icon: 'i-lucide-trophy',
    dotClass: 'bg-n-teal-9',
    textClass: 'text-n-teal-11',
  },
  lost: {
    icon: 'i-lucide-x-circle',
    dotClass: 'bg-n-ruby-9',
    textClass: 'text-n-ruby-11',
  },
  open: {
    icon: 'i-lucide-circle-dot',
    dotClass: 'bg-n-slate-8',
    textClass: 'text-n-slate-11',
  },
};
const accentFor = behavior =>
  BEHAVIOR_ACCENTS[behavior] || BEHAVIOR_ACCENTS.open;

// Realce da oportunidade que pertence à conversa aberta no momento.
const isCurrent = opportunity =>
  props.conversationId != null &&
  Number(opportunity.conversation_id) === Number(props.conversationId);

const hasOpportunities = computed(() => opportunities.value.length > 0);

const loadOpportunities = async () => {
  if (!props.contactId) {
    opportunities.value = [];
    return;
  }
  try {
    opportunities.value = await store.dispatch('pipelines/fetchOpportunities', {
      contactId: props.contactId,
    });
  } catch (error) {
    // Silencioso: é um widget auxiliar — sem oportunidades, não renderiza nada.
    opportunities.value = [];
  }
};

watch(
  () => props.contactId,
  () => loadOpportunities(),
  { immediate: true }
);

// --- Modal -----------------------------------------------------------------
const opportunityDialog = ref(null);
const activeOpportunity = ref(null);

// Mapeia a oportunidade do endpoint para o shape do dialog (display_id vem como
// conversation_display_id; pipeline_name vira o "estágio" exibido junto).
// Mantém também os ids brutos (pipeline_id/conversation_id/conversation_display_id)
// que o modal precisa para editar o valor (updateCard) e navegar p/ o funil.
const dialogOpportunity = computed(() => {
  const opportunity = activeOpportunity.value;
  if (!opportunity) return null;
  return {
    display_id: opportunity.conversation_display_id,
    contact_name: opportunity.pipeline_name,
    stage_name: opportunity.stage_name,
    behavior: opportunity.behavior,
    probability: opportunity.probability,
    value: opportunity.value,
    pipeline_id: opportunity.pipeline_id,
    conversation_id: opportunity.conversation_id,
    conversation_display_id: opportunity.conversation_display_id,
  };
});

const openDialog = opportunity => {
  activeOpportunity.value = opportunity;
  opportunityDialog.value?.open();
};

const onViewConversation = displayId => {
  opportunityDialog.value?.close();
  router.push({
    name: 'inbox_conversation',
    params: { accountId, conversation_id: displayId },
  });
};

// Edição de valor a partir do painel do contato: persiste via updateCard e
// recarrega as oportunidades para refletir o novo valor na lista e no modal.
const onSetValue = async ({ opportunity, value }) => {
  if (!opportunity?.pipeline_id || !opportunity?.conversation_id) return;
  try {
    await store.dispatch('pipelines/updateCard', {
      pipelineId: opportunity.pipeline_id,
      conversationId: opportunity.conversation_id,
      value,
    });
    await loadOpportunities();
    // Re-aponta a oportunidade ativa para o registro recarregado (valor novo).
    if (activeOpportunity.value) {
      const updated = opportunities.value.find(
        item =>
          Number(item.conversation_id) ===
          Number(activeOpportunity.value.conversation_id)
      );
      if (updated) activeOpportunity.value = updated;
    }
  } catch (error) {
    useAlert(t('PIPELINES.DIALOG.VALUE_ERROR'));
  }
};

// "Ver no funil": abre o board do pipeline da oportunidade já filtrado por este
// contato (query contact_id; o PipelineBoard resolve o chip e aplica o filtro).
const onViewPipeline = opportunity => {
  if (!opportunity?.pipeline_id || props.contactId == null) return;
  opportunityDialog.value?.close();
  router.push({
    name: 'pipeline_board',
    params: { accountId, pipelineId: opportunity.pipeline_id },
    query: { contact_id: props.contactId },
  });
};
</script>

<template>
  <div class="conversation--actions">
    <!-- SEM prop `icon`: o AccordionItem roteia `icon` pro EmojiOrIcon/<fluent-icon> (sistema
         antigo de SVG do Chatwoot, nomes tipo `add`/`subtract`). Passar uma classe UnoCSS
         `i-lucide-*` aqui quebra o Icon.vue (`pathSource` lê undefined) e cascateia em erros
         de patch. As demais seções do ContactPanel também não passam `icon`. -->
    <AccordionItem
      v-if="hasOpportunities"
      class="mb-3"
      :title="t('PIPELINES.WIDGET.TITLE')"
      :is-open="isContactSidebarItemOpen('is_devkit_pipeline_open')"
      compact
      @toggle="value => toggleSidebarUIState('is_devkit_pipeline_open', value)"
    >
      <ul class="flex flex-col px-2 py-2 list-none">
        <li
          v-for="opportunity in opportunities"
          :key="opportunity.conversation_id"
        >
          <button
            type="button"
            class="flex flex-col w-full gap-1 px-2 py-2 text-left rounded-md hover:bg-n-alpha-1"
            :class="{ 'bg-n-alpha-2': isCurrent(opportunity) }"
            :title="t('PIPELINES.WIDGET.VIEW_OPPORTUNITY')"
            @click="openDialog(opportunity)"
          >
            <span class="flex items-center gap-1.5 min-w-0">
              <span
                class="size-3.5 flex-shrink-0 i-lucide-workflow text-n-slate-10"
              />
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ opportunity.pipeline_name }}
              </span>
              <span
                v-if="isCurrent(opportunity)"
                class="px-1.5 py-0.5 text-[10px] font-medium rounded text-n-brand bg-n-alpha-2 flex-shrink-0"
              >
                {{ t('PIPELINES.WIDGET.CURRENT_BADGE') }}
              </span>
            </span>
            <span class="flex items-center justify-between gap-2">
              <span class="flex items-center gap-1.5 min-w-0">
                <span
                  class="size-2 rounded-full flex-shrink-0"
                  :class="accentFor(opportunity.behavior).dotClass"
                />
                <span
                  class="text-xs truncate"
                  :class="accentFor(opportunity.behavior).textClass"
                >
                  {{ opportunity.stage_name }}
                </span>
              </span>
              <span
                class="text-xs font-medium tabular-nums text-n-slate-11 flex-shrink-0"
              >
                {{ formatMoney(opportunity.value) }}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </AccordionItem>

    <!-- Dialog SEMPRE montado (fechado) dentro da raiz ESTÁVEL única. Evita as DUAS
         armadilhas do Vue: (1) Teleport dentro de v-if -> "parentNode null"; (2) raiz em
         fragment com v-if -> "emitsOptions null" no diff de bloco. Por isso a raiz é um
         <div> fixo e o v-if fica só no AccordionItem interno. -->
    <PipelineOpportunityDialog
      ref="opportunityDialog"
      :opportunity="dialogOpportunity"
      :contact-id="contactId"
      editable-value
      show-view-pipeline
      @view-conversation="onViewConversation"
      @set-value="onSetValue"
      @view-pipeline="onViewPipeline"
    />
  </div>
</template>
